import { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '@/lib/api';
import { useRealtime } from '@/providers/RealtimeProvider';
import { useGlobalCompany } from '@/hooks/useGlobalCompany';

/**
 * Fetches the total unread inbound SMS count across all Twilio/VoipMs conversations
 * for the current company. Call refresh() to re-fetch on demand (e.g. when leaving
 * the messaging tab or after marking a conversation as read).
 */
export function useVoipUnreadCount(activeTab: string) {
    const [count, setCount] = useState(0);
    const { subscribeNewSmsBackground } = useRealtime();
    const { selectedCompanyId, isLoading: companyLoading } = useGlobalCompany();

    const refresh = useCallback(async () => {
        try {
            const result = await apiService.getConversationsV2(undefined, 100);
            const total = result.items.reduce((sum, c) => sum + c.unreadInboundCount, 0);
            setCount(total);
        } catch {
            // Non-fatal: badge just stays stale
        }
    }, []);

    // `undefined` = never fetched; `null` = fetched with no company selected.
    const fetchedCompanyRef = useRef<string | null | undefined>(undefined);
    const prevTabRef = useRef<string | null>(null);

    // Only fetch on first load for a company and when returning from the messaging tab (where
    // conversations get marked read). The realtime subscription below keeps the badge current
    // in between, so refetching 100 conversation DTOs on every tab switch bought nothing.
    useEffect(() => {
        if (companyLoading) return;

        const prevTab = prevTabRef.current;
        prevTabRef.current = activeTab;

        if (activeTab === 'messaging') {
            setCount(0);
            return;
        }

        const companyId = selectedCompanyId ?? null;
        const isNewCompany = fetchedCompanyRef.current !== companyId;
        const leftMessaging = prevTab === 'messaging';
        if (!isNewCompany && !leftMessaging) return;

        fetchedCompanyRef.current = companyId;
        void refresh();
    }, [activeTab, companyLoading, selectedCompanyId, refresh]);

    useEffect(() => {
        const unsubscribe = subscribeNewSmsBackground((event) => {
            if (selectedCompanyId && event.company_id !== selectedCompanyId) {
                return;
            }

            if (activeTab !== 'messaging') {
                setCount((prev) => prev + 1);
            }
        });

        return unsubscribe;
    }, [subscribeNewSmsBackground, activeTab, selectedCompanyId]);

    return { count, refresh };
}
